import { useState, useEffect } from 'react';
import { useCountry } from '../../contexts/CountryContext';
import { api } from '../../lib/api';
import { formatDate, formatCurrency, getStatusColor, WASTE_STREAM_LABELS } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, ClipboardList, Trash2 } from 'lucide-react';

export function AssignmentsPage() {
  const { selectedCountry } = useCountry();
  const [assignments, setAssignments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    vendor_id: '',
    store_id: '',
    country_code: '',
    waste_stream: '',
    service_type: '',
    start_date: '',
    price: '',
    currency: 'EUR',
  });
  const eligibleVendors = vendors.filter(
    (vendor) => vendor.status === 'qualified' || vendor.status === 'active'
  );

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCountry ? { country_code: selectedCountry } : {};
      const [assignmentsData, vendorsData, storesData] = await Promise.all([
        api.getAssignments(params),
        api.getVendors(),
        api.getStores(params),
      ]);
      setAssignments(assignmentsData);
      setVendors(vendorsData);
      setStores(storesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    try {
      const store = stores.find(s => s.id === formData.store_id);
      await api.createAssignment({
        ...formData,
        country_code: store?.country_code || formData.country_code,
        start_date: new Date(formData.start_date).toISOString(),
        price: parseFloat(formData.price) || 0,
      });
      toast.success('Assignment created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Delete this assignment? This cannot be undone.')) {
      return;
    }

    try {
      await api.deleteAssignment(assignmentId);
      toast.success('Assignment deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || '-';
  const getStoreName = (id) => stores.find(s => s.id === id)?.name || '-';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assignments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Assignments</h1>
          <p className="text-muted-foreground mt-1">
            Manage vendor-to-store service assignments
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-assignment-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Assignment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create Assignment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAssignment} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vendor *</Label>
                  <Select
                    value={formData.vendor_id}
                    onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleVendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    Only qualified or active vendors can be assigned.
                  </p>
                </div>
                <div>
                  <Label>Store *</Label>
                  <Select
                    value={formData.store_id}
                    onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map((store) => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name} ({store.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Waste Stream *</Label>
                  <Select
                    value={formData.waste_stream}
                    onValueChange={(value) => setFormData({ ...formData, waste_stream: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(WASTE_STREAM_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Service Type *</Label>
                  <Input
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    placeholder="e.g., Weekly Collection"
                    required
                  />
                </div>
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Price (EUR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Assignment</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Waste Stream</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : assignments.length === 0 ? (
                <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                    <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No assignments found</p>
                  </TableCell>
                </TableRow>
              ) : (
                assignments.map((assignment) => (
                  <TableRow key={assignment.id}>
                    <TableCell className="font-medium">{getVendorName(assignment.vendor_id)}</TableCell>
                    <TableCell>{getStoreName(assignment.store_id)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {WASTE_STREAM_LABELS[assignment.waste_stream] || assignment.waste_stream}
                      </Badge>
                    </TableCell>
                    <TableCell>{assignment.service_type}</TableCell>
                    <TableCell>{formatDate(assignment.start_date)}</TableCell>
                    <TableCell>{formatCurrency(assignment.price, assignment.currency)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(assignment.status)}>
                        {assignment.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteAssignment(assignment.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
